import { Fragment, useCallback, useEffect, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import Segmented from "@/components/Segmented";
import { Loader2, Plus, Share, Mail, ChevronRight, ChevronDown, ChevronLeft, ArrowLeft, Minimize2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi, money, pct } from "../../lib/api";
import CoBuilder from "./CoBuilder";
import CoMarginDefaults from "./CoMarginDefaults";
import FrameworkEmailDialog from "../../components/FrameworkEmailDialog";
import PendingChangeItems from "../../../drawings/PendingChangeItems";
import Hint from "@/components/Hint";

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-600",
  submitted: "bg-sky-100 text-sky-700",
  process_unapproved: "bg-amber-100 text-amber-800",
  process_approved: "bg-emerald-100 text-emerald-700",
  revise_resubmit: "bg-violet-100 text-violet-700",
  rejected: "bg-rose-100 text-rose-700",
  void: "bg-slate-200 text-slate-500",
};
const AGE_TONE = { red: "text-rose-700 font-semibold", amber: "text-amber-700", ok: "text-slate-500" };
const FRS_ICON = {
  synced: [Check, "text-emerald-600", "FRS updated for this change"],
  not_applicable: [Check, "text-emerald-600/60", "No room change — nothing to update in the FRS"],
  overridden: [Check, "text-amber-500", "FRS review overridden with a reason"],
  task_created: [X, "text-rose-600", "FRS Update task raised — FRS not updated yet"],
  not_reviewed: [X, "text-rose-600", "FRS not updated for this change"],
};
const FrsMark = ({ state }) => {
  const [Icon, cls, title] = FRS_ICON[state] || FRS_ICON.not_reviewed;
  return <span title={title} className="inline-flex"><Icon className={`h-4 w-4 ${cls}`} /></span>;
};

/** OPS-3 — one tab. The log and the builder live together, never in two places. */
export default function ChangeOrdersTab({ project, reload }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useUrlState("opsco", "");
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [draft, setDraft] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  // The open CO fills the tab by default; the top-right toggle collapses it back into the log row.
  const [focus, setFocus] = useState(() => localStorage.getItem("ops.co.focus") !== "0");
  const toggleFocus = () => setFocus((f) => { localStorage.setItem("ops.co.focus", f ? "0" : "1"); return !f; });

  const load = useCallback(async () => {
    try { setData(await opsApi.changeOrders(project.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the change orders"); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  // The open change order is a URL entry (?opsco=<id>) so Back closes it and Forward reopens it.
  const open = (id) => setOpenId(openId === id ? "" : id);
  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    if (detail?.id === openId) return;
    setDetail(null);
    opsApi.changeOrder(project.id, openId).then(setDetail).catch(() => toast.error("Could not open that change order"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, project.id]);

  const create = async () => {
    setCreating(true);
    try {
      const co = await opsApi.createChangeOrder(project.id, { title: title.trim() });
      setTitle("");
      toast.success(`${co.number} created`);
      await load();
      setDetail(co);
      setOpenId(co.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create that change order"); }
    finally { setCreating(false); }
  };

  const emailLog = async () => {
    try {
      const d = await opsApi.emailDraft(project.id, "co_log");
      setDraft({ ...d, ref: { kind: "co_log" } });
    } catch { toast.error("Could not prepare that email"); }
  };

  const changed = async (co) => {
    setDetail(co);
    await load();
    reload?.();
  };

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading change orders…
    </div>;
  }

  const rows = data.change_orders.filter((c) => !!c.archived === showArchived);
  const s = data.summary;
  const openRow = openId ? data.change_orders.find((c) => c.id === openId) : null;
  const coRows = rows;
  const coIdx = coRows.findIndex((c) => c.id === openId);

  if (openId && focus) {
    return (
      <div className="space-y-4" data-testid="ops-co-focus">
        <div className="flex items-center gap-2 text-sm">
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setOpenId("")}
                  data-testid="ops-co-focus-back">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Change orders
          </Button>
          <span className="text-slate-300">/</span>
          <span className="font-mono text-xs text-slate-600">{openRow?.number || "…"}</span>
          <span className="ml-2 flex items-center gap-1">
            <button type="button" data-kb-prev disabled={coIdx <= 0} onClick={() => setOpenId(coRows[coIdx - 1].id)}
                    data-testid="ops-co-focus-prev" title="Previous change order (←)"
                    className="rounded-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:opacity-30">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button type="button" data-kb-next disabled={coIdx < 0 || coIdx >= coRows.length - 1} onClick={() => setOpenId(coRows[coIdx + 1].id)}
                    data-testid="ops-co-focus-next" title="Next change order (→)"
                    className="rounded-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:opacity-30">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            {coIdx >= 0 && <span className="text-[11px] text-slate-400">{coIdx + 1} / {coRows.length}</span>}
          </span>
          <button type="button" onClick={toggleFocus} data-testid="ops-co-collapse"
                  title="Collapse back into the change order log"
                  className="ml-auto rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:border-slate-700 dark:hover:bg-slate-800">
            <Minimize2 className="h-3.5 w-3.5" />
          </button>
        </div>
        {detail ? (
          <CoBuilder project={project} co={detail} onChanged={changed}
                     canEdit={data.can_edit} canOverrideMargin={data.can_override_margin}
                     focused />
        ) : (
          <div className="flex items-center gap-2 py-6 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening…
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ops-co-tab">
      <PendingChangeItems projectId={project.id} changeOrders={data.change_orders} onChanged={load} />
      {/* exposure header */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[["Approved", s.approved, "text-emerald-700", "ops-co-total-approved",
           "Processed Approved change orders — in the revised contract and billable"],
          ["Submitted / Processed Unapproved", s.pending, "text-amber-700", "ops-co-total-pending",
           "Work we have asked to be paid for that is not executed yet: Submitted + Processed Unapproved. Not in the contract, not billable."],
          ["Rejected / void", s.rejected, "text-slate-500", "ops-co-total-rejected",
           "Refused or withdrawn — kept in the log for the record"],
          ["Revised contract", (data.contract_value || 0) + s.approved, "text-slate-900",
           "ops-co-revised-contract",
           "Original contract plus every Processed Approved change order"]].map(
          ([label, value, tone, tid, hint]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white px-4 py-3"
               title={hint} data-testid={tid}>
            <p className="text-xs text-slate-500">{label}</p>
            <p className={`text-lg font-semibold ${tone}`}>{money(value)}</p>
          </div>
        ))}
      </div>

      {s.pending > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
             data-testid="ops-co-pending-warning">
          {money(s.pending)} of change orders is submitted or Processed Unapproved — none of it is
          in the revised contract, and none of it can be billed.
          {s.overdue > 0 && ` ${s.overdue} of them are more than 30 days outstanding.`}
        </div>
      )}

      {/* controls */}
      <div className="flex flex-wrap items-center gap-2">
        {data.can_edit && (
          <>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="New change order title" className="h-8 w-64 text-sm"
                   data-testid="ops-co-new-title" />
            <Button size="sm" className="h-8 px-2.5 text-xs" onClick={create} disabled={creating} data-testid="ops-co-new" title="New change order">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
          </>
        )}
        <Button variant="data" size="sm" className="text-xs" data-testid="ops-co-export-xlsx"
                onClick={() => opsApi.coLogDownload(project.id, "xlsx")
                  .catch(() => toast.error("The Excel log could not be downloaded"))}>
          <Share className="mr-1.5 h-3.5 w-3.5" /> Excel
        </Button>
        <Button variant="data" size="sm" className="text-xs" data-testid="ops-co-export-pdf"
                onClick={() => opsApi.coLogDownload(project.id, "pdf")
                  .catch(() => toast.error("The PDF log could not be downloaded"))}>
          <Share className="mr-1.5 h-3.5 w-3.5" /> PDF
        </Button>
        <Button variant="data" size="sm" className="text-xs" onClick={emailLog} data-testid="ops-co-email-log">
          <Mail className="mr-1.5 h-3.5 w-3.5" /> Log
        </Button>
        <Segmented className="ml-2" value={showArchived ? "archived" : "active"}
                   onChange={(k) => setShowArchived(k === "archived")}
                   options={[{ key: "active", label: "Active", testId: "ops-co-view-active" },
                             { key: "archived", label: "Archived", testId: "ops-co-view-archived" }]} />
        {data.can_edit && <div className="ml-auto"><CoMarginDefaults project={project} reload={reload} /></div>}
      </div>

      {/* log */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[1000px] text-sm" data-testid="ops-co-log">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">CO #</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Scope</th>
              <th className="px-3 py-2 text-right">Value</th>
              <th className="px-3 py-2 text-right">Margin</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left"><Hint text="Date the proposal was emailed to the GC — set automatically">Submitted</Hint></th>
              <th className="px-3 py-2 text-right"><Hint text="Days since the proposal was submitted with no GC decision (amber at 14, red at 30)">Days out</Hint></th>
              <th className="px-3 py-2 text-center"><Hint text="Green check: the FRS was updated for this change (or it changes no rooms). Red X: FRS not updated yet">FRS</Hint></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-500">
                No change orders on this job yet.
              </td></tr>
            )}
            {rows.map((c) => (
              <Fragment key={c.id}>
                <tr onClick={() => open(c.id)}
                    className={`cursor-pointer hover:bg-slate-50 ${c.archived ? "opacity-60" : ""}`}
                    data-testid={`ops-co-row-${c.number}`}>
                  <td className="px-3 py-2 font-mono text-xs">
                    <span className="inline-flex items-center gap-1">
                      {openId === c.id ? <ChevronDown className="h-3 w-3" />
                        : <ChevronRight className="h-3 w-3" />}
                      {c.number}{c.revision > 0 ? ` R${c.revision}` : ""}
                    </span>
                  </td>
                  <td className="px-3 py-2">{c.title}</td>
                  <td className="px-3 py-2 text-xs">{c.scopes.join(", ") || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(c.value)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {pct(c.margin_pct)}
                    {c.margin_overrides > 0 && (
                      <span className="ml-1 text-[10px] text-violet-600" title="margin overridden">*</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_TONE[c.status]}`}>
                      {c.status_label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{(c.submitted_at || "").slice(0, 10) || "—"}</td>
                  <td className={`px-3 py-2 text-right text-xs tabular-nums ${AGE_TONE[c.ageing.tone] || ""}`}>
                    {c.ageing.days ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-center" data-testid={`ops-co-frs-state-${c.number}`} data-state={c.frs_sync_state}>
                    <FrsMark state={c.frs_sync_state} />
                  </td>
                </tr>
                {openId === c.id && (
                  <tr>
                    <td colSpan={9} className="bg-slate-50/70 px-4 py-4">
                      {detail ? (
                        <CoBuilder project={project} co={detail} onChanged={changed}
                                   canEdit={data.can_edit}
                                   canOverrideMargin={data.can_override_margin}
                                   focused={false} onToggleFocus={toggleFocus} />
                      ) : (
                        <div className="flex items-center gap-2 py-6 text-xs text-slate-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening…
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        Only <b>Processed Approved</b> change orders write to the budget, the contract and the
        schedule of values. <b>Processed Unapproved</b> writes to the FRS and releases procurement and
        never billing. Rejected and void change orders stay in this log for good.
      </p>

      {draft && (
        <FrameworkEmailDialog projectId={project.id} draft={draft} onClose={() => setDraft(null)} />
      )}
    </div>
  );
}
